import React from 'react';
import PropTypes from 'prop-types';
import {
    space, colors, fontSizes, lineHeights, fontWeights
} from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import { getImageAltText } from 'utils/Accessibility';
import {
    Grid, Box, Text, Divider, Image, Button, Flex, Link
} from 'components/ui';
import Modal from 'components/Modal/Modal';
import Loader from 'components/Loader/Loader';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import SubscriptionInfoSection from 'components/GlobalModals/ManageSubscriptionModal/components/SubscriptionInfoSection';
import { useManageSubscriptionModal } from 'components/GlobalModals/ManageSubscriptionModal/hooks/useManageSubscriptionModal';

const LEFT_COLUMN_WIDTH = 108;

/**
 * ManageSubscriptionModalRedesign - Accessible modal for managing auto-replenishment subscriptions
 *
 * Allows users to view and modify their subscription details including:
 * - Skip or pause options
 * - Delivery frequency
 * - Shipping address
 * - Payment method
 *
 * @component
 */
function ManageSubscriptionModalRedesign({
    currentSubscription,
    fireGenericErrorAnalytics,
    isOpen,
    onChangeDate,
    onChangeFrequency,
    onChangePayment,
    onChangeShipping,
    onDismiss,
    onPauseSubscription,
    onSkipDelivery,
    onUnsubscribeClick,
    onSkuSwapClick
}) {
    const {
        creditCard, featureFlags, isLoading, localization, onModalClose, product, shippingInfo, shouldShowItemVariation
    } =
        useManageSubscriptionModal({
            currentSubscription,
            onDismiss,
            fireGenericErrorAnalytics
        });

    return (
        <Modal
            width={0}
            onDismiss={onModalClose}
            isOpen={isOpen}
        >
            <Modal.Header>
                <Modal.Title children={localization.manageSubscription} />
            </Modal.Header>
            <Modal.Body
                paddingTop={space[3]}
                paddingBottom={space[4]}
                paddingX={space[4]}
                css={styles.modalBody}
            >
                {isLoading && <Loader isShown={true} />}
                {!isLoading && product && (
                    <>
                        <Grid css={styles.productContainer}>
                            <Box>
                                <ProductImage
                                    id={product.skuId}
                                    size={LEFT_COLUMN_WIDTH}
                                    skuImages={product.skuImages}
                                    disableLazyLoad={true}
                                    altText={getImageAltText(product)}
                                />
                            </Box>
                            <div>
                                <Grid css={[styles.productDetails, styles.baseFont]}>
                                    <Box>
                                        <Text
                                            is='h4'
                                            css={styles.title}
                                            children={product.brandName}
                                            data-at={Sephora?.debug?.dataAt('sku_brand')}
                                        />
                                        <Text
                                            is='p'
                                            css={styles.subtitle}
                                            children={product.productName}
                                            data-at={Sephora?.debug?.dataAt('sku_name')}
                                        />
                                        {shouldShowItemVariation && (
                                            <Text
                                                is='p'
                                                css={[styles.sku, styles.secondaryColor]}
                                                data-at={Sephora?.debug?.dataAt('sku_size')}
                                            >
                                                {product.variationType}: {product.variationValue}
                                            </Text>
                                        )}
                                        <Box>
                                            <Text
                                                css={styles.priceSaving}
                                                data-at={Sephora?.debug?.dataAt('sku_discounted_price')}
                                            >
                                                {product.savingAmount}
                                            </Text>{' '}
                                            <Text
                                                is='del'
                                                data-at={Sephora?.debug?.dataAt('sku_price')}
                                            >
                                                {product.productPrice}
                                            </Text>
                                        </Box>
                                        {featureFlags.enableReplenishmentSkuSwap && (
                                            <Text
                                                is='p'
                                                css={[styles.skuSwap, styles.secondaryColor]}
                                            >
                                                {product.productHasSeveralSkus ? (
                                                    <Link
                                                        css={styles.linkColor}
                                                        children={localization.skuSwapButton}
                                                        onClick={onSkuSwapClick}
                                                        data-testid='sku-swap-trigger'
                                                    />
                                                ) : (
                                                    localization.skuSwapUnavailable
                                                )}
                                            </Text>
                                        )}
                                    </Box>
                                </Grid>
                            </div>
                        </Grid>
                        <Flex css={styles.contentContainer}>
                            <Flex css={styles.operations}>
                                <Text
                                    is='h4'
                                    css={styles.nextShipment}
                                >
                                    {localization.nextShipment} <Text css={styles.nextShipmentDate}>{shippingInfo.nextShipmentDate}</Text>
                                </Text>

                                {featureFlags.enableReplenishmentDateModifiable && (
                                    <Button
                                        variant='primary'
                                        children={localization.changeDate}
                                        onClick={onChangeDate}
                                    />
                                )}
                                {featureFlags.enableReplenishmentOperations && (
                                    <Flex gap={space[3]}>
                                        <Button
                                            size='sm'
                                            variant='secondary'
                                            css={styles.operationButton}
                                            onClick={onSkipDelivery}
                                            children={localization.skip}
                                        />
                                        <Button
                                            size='sm'
                                            variant='secondary'
                                            css={styles.operationButton}
                                            onClick={onPauseSubscription}
                                            children={localization.pause}
                                        />
                                    </Flex>
                                )}
                            </Flex>

                            <Divider />

                            <SubscriptionInfoSection
                                ariaLabel={`${localization.deliveryTitle}: ${localization.deliveryValue}`}
                                isInteractive={featureFlags.enableReplenishmentFrequencyModifiable}
                                onClick={onChangeFrequency}
                                title={localization.deliveryTitle}
                            >
                                <Text css={styles.baseFont}>{localization.deliveryValue}</Text>
                            </SubscriptionInfoSection>

                            <Divider />

                            <SubscriptionInfoSection
                                ariaLabel={`${localization.shippingTitle}: ${shippingInfo.fullName}, ${shippingInfo.fullAddress}`}
                                isInteractive={featureFlags.enableReplenishmentAddressModifiable}
                                onClick={onChangeShipping}
                                title={localization.shippingTitle}
                            >
                                <Text
                                    is='p'
                                    fontWeight={fontWeights.bold}
                                >
                                    {shippingInfo.fullName}
                                </Text>
                                <Text is='p'>
                                    {shippingInfo.addressLine1}
                                    <br />
                                    {shippingInfo.fullAddress}
                                    <br />
                                    {shippingInfo.phoneNumber}
                                </Text>
                            </SubscriptionInfoSection>

                            <Divider />

                            <SubscriptionInfoSection
                                ariaLabel={`${localization.paymentTitle}: ${creditCard.label}`}
                                isInteractive={featureFlags.enableReplenishmentPaymentModifiable}
                                onClick={onChangePayment}
                                title={localization.paymentTitle}
                            >
                                <Flex gap={space[3]}>
                                    <Image
                                        height={32}
                                        width={50}
                                        src={creditCard.logo}
                                        alt={creditCard.alt}
                                    />
                                    <Text is='p'>
                                        {creditCard.label}
                                        <br />
                                        <span css={styles.smFont}>
                                            {localization.creditCardExpDate} {creditCard.expDate}
                                        </span>
                                    </Text>
                                </Flex>
                            </SubscriptionInfoSection>

                            <Text
                                is='p'
                                css={[styles.legalCopy, styles.smFont]}
                            >
                                {localization.legalCopy}
                            </Text>

                            <Divider />

                            <Link
                                css={[styles.cancelSubscription, styles.linkColor]}
                                onClick={onUnsubscribeClick}
                            >
                                {localization.cancelSubscription}
                            </Link>
                        </Flex>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}

const styles = {
    modalBody: {
        lineHeight: lineHeights.tight,
        minHeight: '400px'
    },

    // Product Section
    productContainer: {
        alignItems: 'start',
        gap: space[3],
        gridTemplateColumns: 'auto 1fr',
        marginBottom: space[4],
        marginTop: space[3]
    },
    productDetails: {
        gridTemplateColumns: 'auto 1fr'
    },
    title: {
        fontWeight: fontWeights.bold,
        marginBottom: '2px'
    },
    subtitle: {
        marginBottom: '2px'
    },
    sku: {
        marginBottom: space[2]
    },
    priceSaving: {
        fontWeight: fontWeights.bold,
        color: colors.red
    },
    skuSwap: {
        marginTop: space[2]
    },

    // Operations Section (ChangeDate/Skip/Pause buttons)
    nextShipment: {
        fontWeight: fontWeights.bold
    },
    nextShipmentDate: {
        color: colors.green
    },
    operations: {
        flexDirection: 'column',
        gap: space[2]
    },
    operationButton: {
        flexGrow: 1
    },

    // Tab Section (Delivery, Shipping, Payment)
    contentContainer: {
        flexDirection: 'column',
        gap: space[4]
    },

    // Footer
    legalCopy: {
        marginTop: -space[2]
    },
    cancelSubscription: {
        marginRight: 'auto'
    },

    // Shared/Utility
    smFont: {
        fontSize: fontSizes.sm
    },
    baseFont: {
        fontSize: fontSizes.base
    },
    secondaryColor: {
        color: colors.gray
    },
    linkColor: {
        color: colors.blue
    }
};

ManageSubscriptionModalRedesign.propTypes = {
    currentSubscription: PropTypes.shape({
        frequency: PropTypes.number,
        frequencyType: PropTypes.string,
        items: PropTypes.array,
        paymentId: PropTypes.string,
        profileId: PropTypes.string,
        shippingAddressId: PropTypes.string
    }).isRequired,
    fireGenericErrorAnalytics: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onChangeDate: PropTypes.func.isRequired,
    onChangeFrequency: PropTypes.func.isRequired,
    onChangePayment: PropTypes.func.isRequired,
    onChangeShipping: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onPauseSubscription: PropTypes.func.isRequired,
    onSkipDelivery: PropTypes.func.isRequired,
    onSkuSwapClick: PropTypes.func.isRequired,
    onUnsubscribeClick: PropTypes.func.isRequired
};

export default wrapFunctionalComponent(ManageSubscriptionModalRedesign, 'ManageSubscriptionModalRedesign');
